// lib/cart/cartActions.ts
// Universal "Add to cart" dispatcher. Reads each client's cart strategy and
// invokes the right behavior (call a global function, hit an AJAX endpoint,
// navigate, or no-op).
//
// This module runs in the browser (script-injected widget context). It uses
// `window` and `fetch` directly. All call sites must be client components.

import type { CartConfig } from '@/lib/clients/types';

export interface AddToCartInput {
  productId: string | number;
  quantity?: number;
  /** Used by `navigate` strategy as the URL to open. Optional otherwise. */
  productUrl?: string;
}

export interface AddToCartResult {
  success: boolean;
  /** Strategy that was attempted, useful for logging/UX. */
  strategy: CartConfig['strategy'];
  /** Human-readable error message when success is false. */
  error?: string;
}

/**
 * Dispatch an add-to-cart action based on the client's cart strategy.
 *
 * Returns a result rather than throwing — the UI decides how to handle failures
 * (toast, silent log, or strategy-specific feedback). Strategies that have
 * 'auto' feedback (e.g. OpenCart updates the header automatically) require no
 * UI feedback on success.
 */
export async function addToCart(
  input: AddToCartInput,
  config: CartConfig,
): Promise<AddToCartResult> {
  const quantity = input.quantity ?? config.defaultQuantity ?? 1;
  const strategy = config.strategy;

  if (typeof window === 'undefined') {
    // SSR safety — this should never run server-side, but bail cleanly if it does.
    return { success: false, strategy, error: 'addToCart called outside the browser' };
  }

  switch (strategy) {
    case 'opencart_global':
    case 'custom_callback': {
      const path = config.functionName;
      if (!path) {
        return { success: false, strategy, error: 'functionName missing in cart_config' };
      }
      const fn = resolveFunctionPath(window, path);
      if (!fn) {
        return { success: false, strategy, error: `function "${path}" not found on window` };
      }
      try {
        await fn(input.productId, quantity);
        return { success: true, strategy };
      } catch (err) {
        return {
          success: false,
          strategy,
          error: err instanceof Error ? err.message : 'cart function threw',
        };
      }
    }

    case 'shopify_ajax': {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: input.productId, quantity }),
        });
        if (!res.ok) {
          return { success: false, strategy, error: `Shopify cart returned ${res.status}` };
        }
        return { success: true, strategy };
      } catch (err) {
        return {
          success: false,
          strategy,
          error: err instanceof Error ? err.message : 'shopify cart fetch failed',
        };
      }
    }

    case 'woocommerce_ajax': {
      try {
        const params = new URLSearchParams();
        params.set('product_id', String(input.productId));
        params.set('quantity', String(quantity));
        const res = await fetch('/?wc-ajax=add_to_cart', {
          method: 'POST',
          credentials: 'same-origin',
          body: params,
        });
        if (!res.ok) {
          return { success: false, strategy, error: `WooCommerce cart returned ${res.status}` };
        }
        return { success: true, strategy };
      } catch (err) {
        return {
          success: false,
          strategy,
          error: err instanceof Error ? err.message : 'woocommerce cart fetch failed',
        };
      }
    }

    case 'navigate': {
      if (!input.productUrl) {
        return { success: false, strategy, error: 'productUrl missing for navigate strategy' };
      }
      window.open(input.productUrl, '_blank', 'noopener,noreferrer');
      return { success: true, strategy };
    }

    case 'none':
      return { success: true, strategy };

    default: {
      // Exhaustiveness guard — TypeScript will complain if a strategy is added
      // and not handled. At runtime, treat unknown strategies as a no-op failure.
      const exhaustive: never = strategy;
      return { success: false, strategy: exhaustive, error: `unknown cart strategy: ${exhaustive}` };
    }
  }
}

/**
 * Resolve a dotted path like "cart.add" against a root object and return the
 * leaf function, bound to its parent (so `this` works correctly when invoking).
 * Returns null if any part of the path is missing or the leaf isn't a function.
 */
export function resolveFunctionPath(root: unknown, path: string): ((...args: unknown[]) => unknown) | null {
  const parts = path.split('.');
  let target: any = root;
  for (let i = 0; i < parts.length - 1; i++) {
    target = target?.[parts[i]];
    if (target == null) return null;
  }
  const leafKey = parts[parts.length - 1];
  const fn = target?.[leafKey];
  if (typeof fn !== 'function') return null;
  return fn.bind(target);
}

/**
 * Whether the "Add to cart" button should be shown at all for this config.
 * Currently only false for strategy 'none'.
 */
export function shouldShowCartButton(config: CartConfig): boolean {
  return config.strategy !== 'none';
}
